import React, { useState } from 'react';
import { Modal, Button, Progress, Tag, Flex, Typography, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import dayjs from 'dayjs';
import { getTheme } from 'helpers/getTheme';
import Assets from 'assets/icons';
import { useAppSelector } from 'store/hook';
import { IActiveSubscriptionItem } from 'interfaces';
import billing, { paymentUrl } from 'api/billing';
import colors from 'scss/variables/colors.module.scss';
import CancelSubscriptionModal from '../CancelSubscriptionModal/CancelSubscriptionModal';
import classes from './SubscriptionInfoModal.module.scss';

const paymentType =
  window.REACT_APP_PAYMENT_TYPE !== 'REPLACE_REACT_APP_PAYMENT_TYPE'
    ? window.REACT_APP_PAYMENT_TYPE
    : process.env.REACT_APP_PAYMENT_TYPE || 'robokassa';

interface SubscriptionInfoModalProps {
  subscription: IActiveSubscriptionItem | null;
  isOpen: boolean;
  onClose: () => void;
  onChangePaymentMethod?: () => void;
  onSubscriptionCancelled: () => void;
}

const SubscriptionInfoModal: React.FC<SubscriptionInfoModalProps> = ({
  subscription,
  isOpen,
  onClose,
  onChangePaymentMethod,
  onSubscriptionCancelled,
}) => {
  const { t } = useTranslation();
  const { messageApi } = useAppSelector((state) => state.windowState);

  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [isChangingPaymentMethod, setIsChangingPaymentMethod] = useState<boolean>(false);

  const tariffName = subscription?.tariff_name || subscription?.quote?.tariff_plan_name || 'start';
  const description = subscription?.quote?.name || '';
  const cost = typeof subscription?.cost === 'number' ? subscription.cost : Number(subscription?.cost) || 0;
  const currency = paymentType === 'jusan' ? '₸' : '₽';
  const connectedAt = subscription?.paid_at
    ? dayjs(subscription.paid_at).format('YYYY.MM.DD')
    : subscription?.created_at
    ? dayjs(subscription.created_at).format('YYYY.MM.DD')
    : '——';
  const paymentCard = subscription?.payment_card_num || subscription?.payment_card || '——';
  const nextBillingAt = subscription?.next_billing_at
    ? dayjs(subscription.next_billing_at).format('YYYY.MM.DD')
    : null;
  const isActive = subscription?.subscription_status === 'active' || subscription?.payment_status === 'paid';
  const roomsTotal = subscription?.rooms_total ?? 0;
  const roomsUsed = subscription?.rooms_used ?? (subscription?.rooms?.length || 0);
  const roomsAvailable = subscription?.rooms_available ?? Math.max(roomsTotal - roomsUsed, 0);
  const hasQuota = roomsTotal > 0;
  const quotaPercent = roomsTotal > 0 ? Math.round((roomsUsed / roomsTotal) * 100) : 0;

  const handleCancelModalOpen = () => {
    setIsCancelModalOpen(true);
  };

  const handleSuccessCancel = () => {
    onSubscriptionCancelled();
    onClose();
  };

  const handleChangePaymentMethod = async () => {
    if (onChangePaymentMethod) {
      onChangePaymentMethod();
      return;
    }

    const quoteId =
      subscription?.quote?.id ||
      (subscription as any)?.quote_id

    if (!quoteId) return;

    setIsChangingPaymentMethod(true);
    try {
      const response = await billing.changeSubscriptionPaymentMethod(
        quoteId,
        paymentType
      );
      const { payment_link } = response.data;
      if (typeof payment_link === 'string') {
        window.location.href = payment_link;
      } else if (payment_link && typeof payment_link === 'object') {
        const queryParams: string[] = [];
        for (const key in payment_link) {
          const value = payment_link[key] ?? '';
          queryParams.push(`${key}=${value}`);
        }
        const correctParams = queryParams
          .join('&')
          .replace('&redirect=true', '%26redirect=true');
        window.location.href = `${paymentUrl}?${correctParams}`;
      }
    } catch {
      messageApi?.error(t('Billing.subscriptionInfo.errorLoad'));
    } finally {
      setIsChangingPaymentMethod(false);
    }
  };


  return (
    <>
      <Modal
        open={isOpen}
        onCancel={onClose}
        width={560}
        title={t('Billing.subscriptionInfo.title')}
        footer={null}
        destroyOnClose
        centered
        className={cn(classes.modal, getTheme())}
      >
        {subscription ? (

          <Flex vertical gap={20}>
            <Flex vertical gap={12} className={classes.section}>
              <Typography.Text className={classes.sectionTitle}>
                {t('Billing.subscriptionInfo.paramsTitle')}
              </Typography.Text>
              <Flex vertical gap={10}>
                <Flex justify="space-between" align="center">
                  <Typography.Text className={classes.label}>{t('Billing.subscriptionInfo.tariff')}</Typography.Text>
                  <Typography.Text className={classes.value}>{tariffName}</Typography.Text>
                </Flex>
                {description ? (
                  <Flex justify="space-between" align="center">
                    <Typography.Text className={classes.label}>{t('Billing.subscriptionInfo.description')}</Typography.Text>
                    <Typography.Text className={classes.value}>{description}</Typography.Text>
                  </Flex>
                ) : null}
                <Flex justify="space-between" align="center">
                  <Typography.Text className={classes.label}>{t('Billing.subscriptionInfo.cost')}</Typography.Text>
                  <Typography.Text className={classes.value}>
                    {cost.toLocaleString('ru-RU')} {currency} {t('Billing.subscriptionInfo.perMonth')}
                  </Typography.Text>
                </Flex>
                <Flex justify="space-between" align="center">
                  <Typography.Text className={classes.label}>{t('Billing.subscriptionInfo.connectedAt')}</Typography.Text>
                  <Typography.Text className={classes.value}>{connectedAt}</Typography.Text>
                </Flex>
                <Flex justify="space-between" align="center">
                  <Typography.Text className={classes.label}>{t('Billing.subscriptionInfo.paymentMethod')}</Typography.Text>
                  <Typography.Text className={classes.value}>{paymentCard}</Typography.Text>
                </Flex>
                <Flex justify="space-between" align="center">
                  <Typography.Text className={classes.label}>{t('Billing.subscriptionInfo.nextBilling')}</Typography.Text>
                  <Typography.Text className={classes.value}>
                    {nextBillingAt || '——'}
                  </Typography.Text>
                </Flex>
                <Flex justify="space-between" align="center">
                  <Typography.Text className={classes.label}>{t('Billing.subscriptionInfo.status')}</Typography.Text>
                  <Typography.Text className={classes.value}>
                    {isActive ? (
                      <Tag color="success">{t('Billing.subscriptions.status.active')}</Tag>
                    ) : (
                      <Tag color="error">{t('Billing.subscriptions.status.disabled')}</Tag>
                    )}
                  </Typography.Text>
                </Flex>
              </Flex>
            </Flex>

            {hasQuota ? (
              <Flex vertical gap={12} className={classes.section}>
                <Typography.Text className={classes.sectionTitle}>
                  {t('Billing.subscriptionInfo.roomsTitle')}
                </Typography.Text>
                <Flex vertical gap={10}>
                  <Flex justify="space-between" align="center">
                    <Typography.Text className={classes.label}>
                      {t('Billing.subscriptionInfo.roomsConnected')}
                    </Typography.Text>
                    <Typography.Text className={classes.value}>
                      {roomsUsed} {t('Billing.subscriptionInfo.outOf')}{' '}
                      {roomsTotal}
                    </Typography.Text>
                  </Flex>
                  <Flex justify="space-between" align="center">
                    <Typography.Text className={classes.label}>
                      {t('Billing.subscriptionInfo.roomsAvailable')}
                    </Typography.Text>
                    <Typography.Text className={classes.value}>
                      {roomsAvailable} {t('Billing.subscriptionInfo.roomsCount')}
                    </Typography.Text>
                  </Flex>
                </Flex>
                <Flex vertical gap={6} className={classes.progressWrap}>
                  <Progress
                    percent={quotaPercent}
                    showInfo={false}
                    strokeColor={colors.primary100}
                  />
                  <Typography.Text className={classes.quotaHint}>
                    {t('Billing.subscriptionInfo.quotaHint', {
                      count: roomsTotal,
                    })}
                  </Typography.Text>
                </Flex>
              </Flex>
            ) : null}

            {isActive ? (
              <Flex vertical gap={12} className={classes.section}>
                <Typography.Text className={classes.sectionTitle}>
                  {t('Billing.subscriptionInfo.managementTitle')}
                </Typography.Text>
                <Flex gap={12} align="center" justify="space-between" wrap="wrap">
                  <Button
                    icon={<Assets.CreditCardIcon />}
                    onClick={handleChangePaymentMethod}
                    loading={isChangingPaymentMethod}
                    className={classes.changeCardButton}
                  >
                    {t('Billing.subscriptionInfo.changePaymentMethod')}
                  </Button>
                  <Button
                    danger
                    onClick={handleCancelModalOpen}
                    className={classes.cancelSubButton}
                  >
                    {t('Billing.subscriptionInfo.cancelSubscription')}
                  </Button>
                </Flex>
              </Flex>
            ) : null}

            {nextBillingAt ? (
              <Alert
                type="warning"
                showIcon
                message={t('Billing.subscriptionInfo.warningNotice', {
                  date: nextBillingAt,
                })}
                className={classes.warningBanner}
              />
            ) : null}

            <Flex justify="flex-end" className={classes.footer}>
              <Button type="primary" onClick={onClose} className={classes.closeButton}>
                {t('Billing.subscriptionInfo.close')}
              </Button>
            </Flex>
          </Flex>
        ) : null}
      </Modal>

      <CancelSubscriptionModal
        isOpen={isCancelModalOpen}
        subscriptionId={subscription?.id || null}
        tariffName={tariffName}
        nextBillingAt={nextBillingAt}
        onClose={() => setIsCancelModalOpen(false)}
        onSuccessCancel={handleSuccessCancel}
      />
    </>
  );
};

export default SubscriptionInfoModal;

